import { PlayCircle, ExternalLink } from 'lucide-react';

export const ResultImageContent = ({ data }: { data: any }) => {
  const imageUrl = data.imageUrl;
  const status = data.status || (imageUrl ? 'COMPLETED' : 'IDLE');
  console.log('ResultImageContent received data:', data);
  console.log('ResultImageContent imageUrl:', JSON.stringify(imageUrl));
  console.log('ResultImageContent status:', status);

  const handleOpenInNewTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!imageUrl) return;
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col gap-2 p-1 w-full h-full">
      <div className="w-full flex-1 min-h-0 bg-black rounded border border-slate-800 flex items-center justify-center overflow-hidden relative group">
        {/* Conditional Rendering Based on Status */}
        {(() => {
          if (status === 'LOADING') {
            return (
              <div className="flex flex-col items-center gap-2 text-slate-600">
                <div className="w-8 h-8 rounded-full border-2 border-blue-700 border-t-blue-500 animate-spin" />
                <span className="text-[10px]">Generating...</span>
              </div>
            );
          } else if (status === 'ERROR') {
            return (
              <div className="flex flex-col items-center gap-2 text-red-600">
                <div className="w-8 h-8 rounded-full border-2 border-red-700 border-t-red-500" />
                <span className="text-[10px]">Generation failed</span>
              </div>
            );
          } else if (imageUrl) {
            return (
              <>
                <img
                  src={imageUrl}
                  alt="Result"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
                {/* Single Action Button - Weakened Style */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
                  {/* Open in New Tab Button */}
                  <button
                    onClick={handleOpenInNewTab}
                    className="w-16 h-16 rounded-full bg-slate-700/60 hover:bg-slate-600/80 text-white/80 hover:text-white flex items-center justify-center transition-all duration-150 transform hover:scale-105"
                    title="Open in New Tab"
                  >
                    <ExternalLink size={24} />
                  </button>
                </div>
              </>
            );
          } else {
            return (
              <div className="flex flex-col items-center gap-2 text-slate-600 animate-pulse">
                <div className="w-8 h-8 rounded-full border-2 border-slate-700 border-t-slate-500 animate-spin" />
                <span className="text-[10px]">Waiting for gen...</span>
              </div>
            );
          }
        })()}
      </div>
      <div className="flex justify-between items-center px-1 shrink-0">
        <div className="text-[10px] text-slate-500">Generated Image</div>
        <div className={`text-[9px] font-mono pr-2 ${status === 'LOADING' ? 'text-blue-400' : status === 'ERROR' ? 'text-red-500' : status === 'COMPLETED' ? 'text-green-500' : 'text-slate-600'}`}>
          #{status}
        </div>
      </div>
    </div >
  );
};

export const ResultVideoContent = ({ data }: { data: any }) => {
  // data.status: 'IDLE' | 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'ERROR'
  // data.progress: number (0-100)
  // data.videoUrl: string
  const { status = 'IDLE', progress = 0, videoUrl } = data;

  return (
    <div className="flex flex-col gap-2 p-1 w-full h-full">
      <div className="w-full flex-1 min-h-0 bg-black rounded border border-slate-800 flex items-center justify-center overflow-hidden relative group">

        {/* STATE: COMPLETED */}
        {status === 'COMPLETED' && videoUrl ? (
          <video
            src={videoUrl}
            className="w-full h-full object-contain"
            controls
            autoPlay
            loop
            muted
          />
        ) : (
          /* STATE: PROCESSING / QUEUED / IDLE */
          <div className="flex flex-col items-center gap-3 text-slate-500 w-full px-4">
            {status === 'PROCESSING' || status === 'QUEUED' ? (
              <>
                <div className="w-8 h-8 rounded-full border-2 border-pink-700 border-t-pink-400 animate-spin" />
                <div className="flex flex-col items-center w-full gap-1">
                  <span className="text-[10px] animate-pulse">
                    {status === 'QUEUED' ? 'Queued...' : `Rendering ${progress}%`}
                  </span>
                  {/* Progress Bar */}
                  <div className="w-full h-1 bg-slate-800 rounded overflow-hidden">
                    <div
                      className="h-full bg-pink-500 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <PlayCircle size={24} className="opacity-20" />
                <span className="text-[10px] opacity-50">Waiting for task...</span>
              </>
            )}
          </div>
        )}

      </div>

      {/* Footer Info */}
      <div className="flex justify-between items-center px-1 shrink-0">
        <div className="text-[10px] text-slate-500">Generated Video</div>
        <div className={`text-[9px] font-mono pr-2 ${status === 'COMPLETED' ? 'text-green-500' :
          status === 'PROCESSING' ? 'text-pink-400' :
            'text-slate-600'
          }`}>
          #{status}
        </div>
      </div>
    </div>
  );
};